import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Button,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import PreviewTableTermination from "./PreviewTableTermination";
import { getInspection, completeReviewPeriod } from "apis/controllers/clearings"; // adjust import path if needed
import TerminationConfirmDialog from "./TerminationConfirmDialog";

const TerminationDialog = ({
  open,
  onClose,
  applicationType,
  seasonId,
  onConfirmSuccess, // optional callback after success

}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [canCloseChecks, setCanCloseChecks] = useState(false);
  const [canArchive, setCanArchive] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);


  // Fetch preview data when dialog opens
  useEffect(() => {
    if (open && seasonId && applicationType?.typeId) {
      setLoading(true);
      setError(null);

      getInspection({ seasonId, typeId: applicationType?.typeId })
        .then((res) => {
          setPreviewData(res?.data?.data);
          setCanCloseChecks(res?.data?.canCloseChecks || false);
          setCanArchive(res?.data?.canArchive || false)
        })
        .catch((err) => {
          console.error("Error fetching inspection:", err);
          setError("Αποτυχία φόρτωσης δεδομένων προεπισκόπησης.");
        })
        .finally(() => setLoading(false));
    }
  }, [open, seasonId, applicationType?.typeId]);

  const handleUpdate = async () => {
    setConfirming(true);
    setError(null);

    try {
      const res = await completeReviewPeriod({ seasonId, typeId: applicationType?.typeId });
      if (res?.status === "error") {
        setError(res?.message || "Αποτυχία ολοκλήρωσης ελέγχων.");
        setOpenConfirm(false);
        return;
      }

      if (onConfirmSuccess) onConfirmSuccess();
      onClose();
      setOpenConfirm(false);
    } catch (err) {
      console.error("Error completing review period:", err);
      setError("Αποτυχία ολοκλήρωσης ελέγχων.");
    } finally {
      setConfirming(false);
    }
  };

  if (!applicationType) return null;



  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <TerminationConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onUpdate={handleUpdate} // 🔹 final update action
        confirming={confirming}
      />
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Ολοκλήρωση Ελέγχων - {applicationType.type}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 2,
          minHeight: '25vh'
        }}
      >
        {/* Warning message */}




        <Typography fontWeight={600} mb={1} mt={1}>
          {`⚠ ΠΡΟΣΟΧΗ! Μετά την Ολοκλήρωση Ελέγχων ΔΕΝ θα είναι δυνατή καμία μεταβολή στις Αιτήσεις του τύπου ${applicationType.type}.`}
        </Typography>


        {/* Loading / Error states */}
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <CircularProgress />
          </Stack>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box
            sx={{
              flex: 1,
              overflowX: "auto",
              overflowY: "auto",
              pr: 1,
            }}
          >
            <PreviewTableTermination rows={previewData} />

          </Box>
        )}

        {/* Warning message */}
        {!loading && !error && canArchive && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              backgroundColor: (theme) => canCloseChecks ? theme.palette.warning.light : theme.palette.error.light,
              borderRadius: 1,
              p: 1.5,
              mb: 2,
              mt: 2,
            }}
          >

            <WarningAmberIcon
              sx={{
                color: (theme) => canCloseChecks ? theme.palette.warning.dark : theme.palette.error.dark,
              }}
            />
            <Typography sx={{ color: "#fefefe" }} fontWeight={600}>
              {canCloseChecks ? "Προσοχή: Μετά την ενημέρωση, οι αιτήσεις θα αλλάξουν κατάσταση!" : "Προσοχή: Δεν μπορείτε να ολοκλήρωσετε τους ελέγχους!"}
            </Typography>
          </Stack>
        )}



        {/* Buttons */}
        {!loading && !error && (
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 1 : 2}
            sx={{ width: "100%", mt: 2 }}
          >
            <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={onClose}
              disabled={confirming}
            >
              Ακύρωση
            </Button>

            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={() => setOpenConfirm(true)}
              disabled={confirming || !canCloseChecks}
              startIcon={confirming ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {confirming ? "Ενημέρωση..." : "Ενημέρωση"}
            </Button>
          </Stack>)}
      </DialogContent>
    </Dialog >
  );
};

export default TerminationDialog;
